import { React, useState, useEffect } from "react";
import { Box } from '@chakra-ui/react';
import { Pencil, Building, RefreshCw } from "lucide-react";
import SidebarScroll from 'components/scroll/SidebarScroll';
import { QuickAlert } from "theme/components/alerts";
import SimpleDataTable from "views/global/SimpleDataTable";
import SimpleSidebar from "views/global/SimpleSidebar";
import FormGenerate from "views/global/FormGenerate";
import { userController } from 'lib/user';
import handleSidebar from "lib/handleSidebar";
import { PropertyOptions } from "lib/PropertyOptions";


function PropertyManagement() {
    //IMPORT CONTROLLER
    const { setAlert, clearAlert, showAlert } = QuickAlert();
    const {saveRecord, getSingleRecord, userDetail} = userController();
    const { getUser } = userDetail();
    const advanceGroup = ["admin","office"].includes(getUser.role);
    
    const [status, setStatus] = useState({
        sidebarForm: false,
        loading: false,
        formLoading: false,
        saveNewList: false,
        editListEnabled: false,
        fetchingProperties: false,
    });
    const [input, setInput] = useState({});
    // Fields that exist ONLY for the auto-generated contract (owner personal data +
    // cadastral details, review/contract-pdf.md §F) — hidden until "Enable Contract"
    // is checked. Everything else (owner name, tax class, entity, CIN, cleaning fee,
    // Gestione WR…) is used elsewhere too (Fattura, owner statements, annual filings)
    // and always shows, regardless of this toggle.
    const CONTRACT_ONLY_FIELDS = [
        "heading_owner_contract",
        "owners",
        "heading_cadastral",
        "is_portion_of_property",
        "cadastral_foglio",
        "cadastral_particella",
        "cadastral_subalterno",
        "cadastral_categoria",
        "cadastral_classe",
        "cadastral_rendita",
    ];
    const contractEnabled = !!input.auto_contract_enabled;
    // Seed one empty owner row the moment "Enable Contract" is checked, so the
    // section never opens empty. Only fires when owners is still empty, so it
    // never overwrites owners already loaded when editing an existing property.
    useEffect(() => {
        if (contractEnabled && (!input.owners || input.owners.length === 0)) {
            setInput(prev => ({
                ...prev,
                owners: [{ name: "", place_of_birth: "", date_of_birth: "", residential_address: "", codice_fiscale: "" }],
            }));
        }
    }, [contractEnabled]);
    const fieldNames = [
        {type: "heading", name:"heading_basic", label: "Informazioni di Base", first: true},
        {label: "Identificativo", name:"identifier", type:"text", required: true, wide: true},
        {label: "Indirizzo", name:"address", type:"text", required: true, wide: true},
        {label: "Descrizione", name:"description", type:"textarea", required: false},
        {label: "Nome sul Campanello", name:"doorbell_name", type:"text", required: false, wide: true},

        {type: "heading", name:"heading_access", label: "Check-In e Accesso"},
        {label: "Note Check-In", name:"checkin_comment", type:"text", required: false},
        {label: "Chiavi", name:"keys", type:"text", required: false},
        {label: "Ascensore", name:"elevator", type:"select", required: true, options: ["si","no"]},
        {label: "Piano", name:"floor", type:"select", required: true, options: [0,1,2,3,4,5,6,7,8,9,10]},
        {label: "Contatore", name:"counter", type:"text", required: false, wide: true},
        {label: "Ingresso", name:"entrance", type:"textarea", required: false},

        {type: "heading", name:"heading_capacity", label: "Capienza e Dimensioni"},
        {label: "Ospiti Massimi", name:"max_guest", type:"select", required: false, options: [1,2,3,4,5,6,7,8,9,10]},
        {label: "Ospiti Ideali", name:"ideal_guest", type:"select", required: false, options: [1,2,3,4,5,6,7,8,9,10]},
        {label: "MQ", name:"mq", type:"text", required: false, wide: true},

        {type: "heading", name:"heading_interior", label: "Interni e Dotazioni"},
        {label: "Ambienti", name:"exterior", type:"multiple", multipleFields:[{name:"label",type:"select", options:PropertyOptions.exterior, placeholder:"ambiente"}, {name:"value",type:"select", options: [1,2,3,4,5,6,7,8,9,10], placeholder:"numero"}], required: false, design: "horizontal"},
        {label: "Elettrodomestici", name:"appliance", type:"multiple", multipleFields:[{name:"label",type:"select", options:PropertyOptions.appliance, placeholder:"elettrodomestico"},{name:"value",type:"text", placeholder:"informazione"}], required: false, design: "horizontal"},
        {label: "Servizi", name:"facilities", type:"multiple", multipleFields:[{name:"label",type:"select", options:PropertyOptions.facilities, placeholder:"servizio"}, {name:"value",type:"text", placeholder:"informazione"}], required: false, design: "horizontal"},
        {label: "Biancheria", name:"laundry", type:"multiple", multipleFields:[{name:"label",type:"text", placeholder:"nome/codice"}, {name:"quantity",type:"select", options: [0.5,1,1.5,2,2.5,3,3.5,4,4.5,5,5.5,6,6.5,7,7.5,8,8.5,9,9.5,10,10.5], placeholder:"qtà"}], required: false, design: "horizontal"},
        {label:"Magazzino Biancheria", name:"warehouse_id", type:"dynamic-select", required: false, src:"/user/dynamic/list/warehouses", placeholder:"magazzino", permissions: ["maintenance"], permissionType :"new", wide: true},

        {type: "heading", name:"heading_ownership", label: "Proprietà e Classificazione"},
        {label: "Proprietario", name:"owner", type:"text", required: false, wide: true},
        {label: "Classe Fiscale", name:"tax_class", type:"dynamic-select", required: false, src:"/user/dynamic/list/tax-classes", placeholder:"classe fiscale", wide: true},
        {label: "Abilita Contratto", name:"auto_contract_enabled", type:"checkbox", required: false, help: "Abilita la generazione automatica del contratto di locazione per questa proprietà."},
        {label: "Gestione WR", name:"is_gestione_wr", type:"checkbox", required: false, help: "Proprietà WR che riceve comunque il rendiconto proprietario"},
        {label: "CIN", name:"cin", type:"text", required: false, wide: true, help: "Codice Identificativo Nazionale — necessario per i rendiconti proprietario e le dichiarazioni annuali"},
        {label: "ID Alloggiati", name:"alloggiati_id", type:"text", required: false, wide: true, help: "ID appartamento su Alloggiati Web — necessario per inviare i dati degli ospiti alla Polizia"},
        {label: "Tipo Contratto", name:"contract_type", type:"text", required: false, wide: true},
        {label: "Note Contratto", name:"contract_note", type:"textarea", required: false},

        {type: "heading", name:"heading_owner_contract", label: "Dati Anagrafici Proprietario (per i Contratti)"},
        {label: "Proprietari", name:"owners", type:"multiple", required: false, boxed: false, itemLabel: "Proprietario", keepFirst: true, help: "Tutti i campi sono obbligatori quando la generazione del contratto è attiva — la maggior parte delle proprietà ha un solo proprietario, alcune (es. comproprietà) ne hanno di più.", multipleFields:[
            {name:"name", type:"text", placeholder:"Nome Completo", required: true},
            {name:"place_of_birth", type:"text", placeholder:"Luogo di Nascita", required: true},
            {name:"date_of_birth", type:"text", placeholder:"Data di Nascita (GG/MM/AAAA)", required: true},
            {name:"residential_address", type:"text", placeholder:"Indirizzo di Residenza", required: true},
            {name:"codice_fiscale", type:"text", placeholder:"Codice Fiscale", required: true},
        ]},

        {type: "heading", name:"heading_cadastral", label: "Dati Catastali (per i Contratti)"},
        {label: "Porzione della Proprietà", name:"is_portion_of_property", type:"checkbox", required: false, help: "Seleziona se il contratto riguarda solo una parte dell'unità catastale."},
        {label: "Foglio Catastale", name:"cadastral_foglio", type:"text", required: false},
        {label: "Particella Catastale", name:"cadastral_particella", type:"text", required: false},
        {label: "Subalterno Catastale", name:"cadastral_subalterno", type:"text", required: false},
        {label: "Categoria Catastale", name:"cadastral_categoria", type:"text", required: false},
        {label: "Classe Catastale", name:"cadastral_classe", type:"text", required: false},
        {label: "Rendita Catastale", name:"cadastral_rendita", type:"number", required: false},

        {type: "heading", name:"heading_financial", label: "Dati Economici"},
        {label: "Costo Pulizie (€, IVA incl.)", name:"cleaning_fee", type:"number", required: false, wide: true},
    ].filter(field => contractEnabled || !CONTRACT_ONLY_FIELDS.includes(field.name))
    const trigerSaveList = status.saveNewList ? false : true;

    //CREATE LIST SHOW & HIDE SIDEBAR
    const createList = () => {
        setStatus(prevStatus => ({
            ...prevStatus,
            sidebarForm: true,
            editListEnabled: false
        }));
        setInput({});
        handleSidebar();
    }
    const hideList = () => {
        setStatus(prevStatus => ({
            ...prevStatus,
            sidebarForm: false,
            editListEnabled: false
        }));
        setInput({});
        handleSidebar(false);
        clearAlert();
    }

    //SAVE LIST DETAIL
    const saveListHandle = () => {
        setStatus(prevStatus => ({
            ...prevStatus,
            loading: true,
        }));
        saveRecord('/user/property/create',input).then(response => {
            return response;
        }).then(response => {
            setStatus(prevStatus => ({
                ...prevStatus,
                loading: false,
                saveNewList: trigerSaveList,
            }));
            hideList();
        }).catch(function(error) {
            setStatus(prevStatus => ({
                ...prevStatus,
                loading: false,
            }));
            setAlert("error",error.response.data.errors);
            SidebarScroll();
        });
    }
    //FETCH PROPERTIES
    const fetchProperties = () => {
        //SET STATUS FETCHING PROPERTIES
        setStatus(prevStatus => ({
            ...prevStatus,
            fetchingProperties: true,
        }));
        //SEND REQUEST
        getSingleRecord("/user/property/fetch").then(response => {
            return response;
        }).then(response => {
            setStatus(prevStatus => ({
                ...prevStatus,
                saveNewList: trigerSaveList,
                fetchingProperties: false,
            }));
        }).catch(function(error) {
            setAlert("error",error.response.data.errors);
            setStatus(prevStatus => ({
                ...prevStatus,
                fetchingProperties: false,
            }));
        });
    }
    //EdIT LIST SAVE
    const editListHandle = (row) => {
        setStatus(prevStatus => ({
            ...prevStatus,
            sidebarForm: true,
            editListEnabled: true,
            formLoading: true
        }));
        handleSidebar();
        getSingleRecord("/user/property/get/"+row.id).then(response => {
            return response;
        }).then(response => {
            setInput((prev) => ({
                ...prev,
                ...response.data.item,
            }));
            setStatus(prevStatus => ({
                ...prevStatus,
                formLoading: false
            }));
        }).catch(function(error) {
            setAlert("error",error.response.data.message);
            setStatus(prevStatus => ({
                ...prevStatus,
                formLoading: false
            }));
        });
    }
    //HIDE SIDEBAR
    useEffect(() => {
        clearAlert();
    }
    ,[status.sidebarForm, status.editListEnabled]);

    const tableMenuItems = [
        {
            icon: <Pencil />,
            title: "Modifica Proprietà",
            handle : editListHandle,
        },
    ];

    const actionButtons = advanceGroup ? [
        {
            title: "Crea Nuova Proprietà",
            icon: <Building />,
            handle: createList,
        },
        {
            title: status.fetchingProperties ? "Sincronizzazione..." : "Sincronizza Proprietà",
            icon: <RefreshCw className={status.fetchingProperties ? "rotate-icon" : "static-icon"} />,
            variant: "brand",
            handle: fetchProperties,
            disabled: status.fetchingProperties,
        },
    ] : false;

    return (
        <Box>
            {status.sidebarForm===false ? showAlert() : null }
            <SimpleDataTable
                title="Gestione Proprietà"
                buttons={actionButtons}
                src="/user/property/list"
                search={true}
                tableMenu={advanceGroup ? tableMenuItems : false}
                trigger={status.saveNewList}
                external={{identifier:'/user/property/management/view/[id]'}}
            />
            <SimpleSidebar
            status={status.sidebarForm}
            title={status.editListEnabled ? "Modifica Proprietà" : "Crea Nuova Proprietà"}
            save={{onClick:saveListHandle,isLoading:status.loading,text:status.editListEnabled ? "Aggiorna" : "Crea Proprietà"}}
            cancel={{onClick:hideList, text:"Annulla"}}
            loading={status.formLoading}
            >
                {status.sidebarForm===false ?  null : showAlert() }
                {status.sidebarForm===true &&
                    <FormGenerate fields={fieldNames} setFields={setInput} defaultValue={input} grid />
                }
            </SimpleSidebar>
        </Box>

    );
}
export default PropertyManagement;
